<script setup>
import { NFlex } from 'naive-ui';
import { computed } from 'vue';

import literacyRec01 from '@/assets/images/result/literacy_rec_01.png';
import literacyRec02 from '@/assets/images/result/literacy_rec_02.png';
import literacyRec03 from '@/assets/images/result/literacy_rec_03.png';
import literacyRec04 from '@/assets/images/result/literacy_rec_04.png';

import numeracyRec01 from '@/assets/images/result/numeracy_rec_01.png';
import numeracyRec02 from '@/assets/images/result/numeracy_rec_02.png';
import numeracyRec03 from '@/assets/images/result/numeracy_rec_03.png';
import numeracyRec04 from '@/assets/images/result/numeracy_rec_04.png';

const props = defineProps({
  type: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  },
  region: { 
    type: String,
    default: ''
  },
  gradeGroup: {
    type: String,
    required: true,
    validator: (value) => ['elementary', 'secondary'].includes(value),
  },
  gradeLevel: {
    type: String,
    default: 'E_4'
  },
  isStudent: { 
    type: Boolean,
    default: false 
  }
});

const sectionTitle = computed(() => {
  const subjectPrefix = props.type === 'literacy' ? '문해력' : '수리력';
  const suggestionType = props.isStudent ? '학습 제언' : '수업 제언';
  return `${subjectPrefix} 신장을 위한 ${suggestionType}`;
});

const suggestionItems = computed(() => {

  if (props.isStudent) {
    if (props.type === 'literacy') {
      // 학생용 문해력 학습 제언 - 학년별
      if (props.gradeLevel === 'E_4') {
        return [{
          icon: literacyRec01,
          pointText: '1. 좋아하는 주제의 글을 꾸준히 읽기',
          descriptionText: '자신이 흥미를 느끼거나 좋아하는 주제를 정해 그와 관련된 책이나 글, 기사 등을 꾸준히 읽습니다. 매일 일정한 시간을 정해 하루에 한 편씩 읽는 습관이 중요합니다. 다양한 형식의 글을 반복적으로 읽고, 읽은 내용을 바탕으로 요약하거나 짧은 글을 써보는 것도 효과적입니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문을 만들고 사전이나 추가 자료 활용하기',
          descriptionText: '글을 읽다가 모르는 단어나 궁금한 내용이 있으면 그냥 넘어가지 말고, 사전이나 신뢰할 수 있는 다양한 자료를 적극적으로 찾아봅니다. 스스로 질문을 만들어 보고 답을 찾아보는 과정을 반복하면서 글에 대한 이해의 폭을 넓힐 수 있습니다. 그리고 새롭게 알게 된 정보나 단어, 자료 출처 등을 간단히 메모하는 습관을 들이면 어휘력과 정보 탐색 능력을 키울 수 있습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 다양한 읽기 전략 사용하기',
          descriptionText: '눈으로만 읽는 것보다 소리 내어 읽으면 내용이 더 잘 기억되고 집중력이 높아집니다. 글을 읽을 때 중요한 의미를 가진 단어나 문장에 밑줄을 긋고 표시하면, 핵심 내용을 쉽게 파악하고 나중에 복습할 때 도움이 됩니다. 읽으면서 떠오르는 생각, 궁금한 점, 느낀 점 등을 간단히 메모하면 사고를 정리하는 능력이 향상됩니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 자기 생각을 표현하기',
          descriptionText: '읽은 내용을 친구나 가족, 선생님 등과 자연스럽게 대화를 나누고 자신의 생각을 말로 정리합니다. 다른 사람과 생각을 공유하고 다양한 의견을 듣는 경험은 자기 생각을 명확하게 만듭니다. 그리고 직접 글로 정리하거나 발표하는 연습을 통해 문해력과 의사소통 능력을 높일 수 있습니다.',
        }];
      } else if (props.gradeLevel === 'E_6') {
        return [{
          icon: literacyRec01,
          pointText: '1. 일상에서 다양한 글 읽기로 생각의 폭 넓히기',
          descriptionText: '흥미를 느끼는 주제뿐만 아니라 일상에서 접하는 공지문, 메뉴판, 웹사이트, 뉴스 등 다양한 매체의 글을 꾸준히 읽습니다. 평소에 다양한 글의 종류와 구조를 경험하는 것이 문해력 향상에 도움이 됩니다. 읽은 내용을 바탕으로 요약하거나 자신의 생각을 표현하는 글을 직접 써보면 효과적입니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문을 만들고 사전이나 추가 자료 활용하기',
          descriptionText: '글을 읽다가 모르는 단어가 있으면 앞뒤 문맥을 활용해 추측해 보고 사전을 통해 정확한 뜻을 확인합니다. 스스로 질문을 만들어 보고 답을 찾아보는 과정을 반복하면서 글에 대한 이해의 폭을 넓힐 수 있습니다. 그리고 새롭게 알게 된 정보나 단어, 자료 출처 등을 간단히 메모하는 습관을 들이면 어휘력과 정보 탐색 능력을 키울 수 있습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 글의 중심 내용 파악과 문제 해결을 위한 읽기 전략 사용하기',
          descriptionText: '글을 소리 내어 읽고 함께 제시된 그림이나 사진을 꼼꼼하게 살펴봅니다. 글에서 중요한 말이나 자주 나오는 내용에 밑줄을 긋거나 표시를 합니다. 제목과 반복되는 표현이 무엇인지 확인하고 글, 그림, 도표의 관계를 비교하면서 내용의 흐름을 파악할 수 있습니다. 일이 일어난 순서나 핵심 정보를 정리하면서 읽으면 글을 더 정확하게 탐색하고 이해하는 데 도움이 됩니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 자기 생각을 표현하고 적극적으로 토론하기',
          descriptionText: '글을 읽으며 글쓴이의 의견이 적절한지 판단하고 간단한 근거를 들어 자신의 생각을 제시합니다. 글의 목적과 주장, 근거가 타당한지 살펴보고, 질문을 던지며 내용을 비판적으로 읽는 습관을 가집니다. 그리고 다른 사람과 내용을 공유하며 이야기를 나누면 자신의 이해도를 점검하는 동시에 새로운 관점을 얻는 데 도움이 됩니다.',
        }];
      } else if (props.gradeLevel === 'M_2') {
        return [{
          icon: literacyRec01,
          pointText: '1. 일상에서의 글 읽기 습관과 연계하기',
          descriptionText: '일상에서 접할 수 있는 각종 안내문, 홍보 글, 카드 뉴스, 인터넷 매체의 다양한 자료를 읽고, 주요 정보를 정리하면서 핵심 내용을 파악해 보는 것은 문해력 향상에 도움이 됩니다. 또한 읽은 내용을 요약하거나, 이와 관련된 짧은 글을 직접 써보면 더욱 효과적입니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 읽는 중에 생긴 질문을 능동적으로 해결하기',
          descriptionText: '글을 읽다가 모르는 단어나 궁금한 점이 생기면 질문을 만들고, 사전이나 자료를 통해 질문에 대한 답을 생각해 보는 습관을 기릅니다. 이 과정에서 글의 의미나 정보를 능동적으로 이해할 수 있습니다. 모르는 단어를 발견했다면 사전을 찾아 뜻과 용례를 확인하고, 글의 맥락을 고려하여 어떤 의미로 이해하면 좋을지 살펴봅니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 다양한 읽기 전략 사용하기',
          descriptionText: '글을 읽기 전에 글의 종류나 주제와 관련된 자신의 배경지식을 떠올리면서 새로운 정보를 연결하면 글을 더 쉽게 이해할 수 있습니다. 제목, 소제목, 사진, 도표 등을 빠르게 훑어보고 전체 구조를 파악하면서 읽으면 읽을 내용에 대한 호기심이 자극되고 집중력도 높아집니다. 중요한 의미를 가진 단어나 문장에 밑줄을 긋고 표시하면서 읽으면 핵심 내용을 쉽게 파악할 수 있습니다. 읽으면서 떠오르는 생각, 질문, 느낌 등에 대해 메모하면 적극적으로 글과 상호작용할 수 있습니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 읽은 내용을 다른 사람과 공유하고 적극적으로 대화하기',
          descriptionText: '다른 사람들과 읽은 내용을 공유하고 대화하는 것은 자신의 읽기 활동을 되돌아보고, 다양한 관점으로 이해의 폭을 넓히는 데 도움이 됩니다. 글의 핵심 내용을 자신의 말로 요약하고 이를 논리적으로 설명하는 과정에서 표현력과 사고력도 함께 키울 수 있습니다.',
        }];
      } else if (props.gradeLevel === 'H_1') {
        return [{
          icon: literacyRec01,
          pointText: '1. 일상에서의 글 읽기 습관과 연계하기',
          descriptionText: '일상에서 접할 수 있는 각종 안내문, 뉴스 기사, 칼럼, 인터넷 매체의 다양한 자료를 읽고, 주요 정보를 정리하면서 핵심 내용을 파악해 보는 것은 문해력 향상에 도움이 됩니다. 또한 읽은 내용을 요약하거나, 이와 관련된 짧은 글을 직접 써보면 더욱 효과적입니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 읽는 중에 생긴 질문을 능동적으로 해결하기',
          descriptionText: '글을 읽다가 모르는 단어나 궁금한 점이 생기면 질문을 만들고, 사전이나 신뢰할 수 있는 자료를 통해 질문에 대한 답을 직접 찾아보는 습관을 기릅니다. 이 과정에서 글을 수동적으로 읽는 수준을 넘어 능동적으로 학습하는 단계로 나아갈 수 있습니다. 모르는 단어를 발견했다면 사전을 찾아 뜻과 용례를 확인하고, 글의 맥락을 고려하여 어떤 의미로 이해하면 좋을지 살펴보면 이해의 폭을 넓힐 수 있습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 다양한 읽기 전략 사용하기',
          descriptionText: '글을 읽기 전에 글의 종류나 주제와 관련된 자신의 배경지식을 떠올리면서 새로운 정보를 연결하면 글을 더 쉽게 이해할 수 있습니다. 제목, 소제목, 사진, 도표 등을 빠르게 훑어보고 전체 구조를 파악하면서 읽으면 읽을 내용에 대한 호기심이 자극되고 집중력도 높아집니다. 중요한 의미를 가진 단어나 문장에 밑줄을 긋고 표시하면서 읽으면 핵심 내용을 쉽게 파악할 수 있습니다. 읽으면서 떠오르는 생각, 질문, 느낌 등에 대해 메모하면 적극적으로 글과 상호작용할 수 있습니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 읽은 내용을 다른 사람과 공유하고 적극적으로 대화하기',
          descriptionText: '다른 사람들과 읽은 내용을 공유하고 대화하는 것은 자신의 이해도를 점검하고 새로운 관점을 이해하는 데 도움이 됩니다. 글의 핵심 내용을 자신의 말로 요약하고 이를 논리적으로 설명하는 과정에서 표현력과 사고력도 함께 키울 수 있습니다.',
        }];
      }
      // 기본값 (초4 문해력)
      return [{
          icon: literacyRec01,
          pointText: '1. 좋아하는 주제의 글을 꾸준히 읽기',
          descriptionText: '자신이 흥미를 느끼거나 좋아하는 주제를 정해 그와 관련된 책이나 글, 기사 등을 꾸준히 읽습니다. 매일 일정한 시간을 정해 하루에 한 편씩 읽는 습관이 중요합니다. 다양한 형식의 글을 반복적으로 읽고, 읽은 내용을 바탕으로 요약하거나 짧은 글을 써보는 것도 효과적입니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문을 만들고 사전이나 추가 자료 활용하기',
          descriptionText: '글을 읽다가 모르는 단어나 궁금한 내용이 있으면 그냥 넘어가지 말고, 사전이나 신뢰할 수 있는 다양한 자료를 적극적으로 찾아봅니다. 스스로 질문을 만들어 보고 답을 찾아보는 과정을 반복하면서 글에 대한 이해의 폭을 넓힐 수 있습니다. 그리고 새롭게 알게 된 정보나 단어, 자료 출처 등을 간단히 메모하는 습관을 들이면 어휘력과 정보 탐색 능력을 키울 수 있습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 다양한 읽기 전략 사용하기',
          descriptionText: '눈으로만 읽는 것보다 소리 내어 읽으면 내용이 더 잘 기억되고 집중력이 높아집니다. 글을 읽을 때 중요한 의미를 가진 단어나 문장에 밑줄을 긋고 표시하면, 핵심 내용을 쉽게 파악하고 나중에 복습할 때 도움이 됩니다. 읽으면서 떠오르는 생각, 궁금한 점, 느낀 점 등을 간단히 메모하면 사고를 정리하는 능력이 향상됩니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 자기 생각을 표현하기',
          descriptionText: '읽은 내용을 친구나 가족, 선생님 등과 자연스럽게 대화를 나누고 자신의 생각을 말로 정리합니다. 다른 사람과 생각을 공유하고 다양한 의견을 듣는 경험은 자기 생각을 명확하게 만듭니다. 그리고 직접 글로 정리하거나 발표하는 연습을 통해 문해력과 의사소통 능력을 높일 수 있습니다.',
        }];
    } else {
      // 학생용 수리력 학습 제언 - 학년별
      if (props.gradeLevel === 'E_4') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 생활 속에서 찾는 수학의 즐거움 느끼기',
          descriptionText: "일상생활 속에서 수학 개념을 적극적으로 찾아봅니다. 계획을 세워 물건을 사거나 용돈을 관리하는 활동은 수학이 우리 생활과 얼마나 밀접한지 알게 해 줍니다. 또한, 주변의 물건을 활용해 도형 놀이를 하거나 규칙적인 변화를 찾아보는 활동은 도형의 특징을 이해하고 규칙을 발견하는 능력을 길러줍니다. '지출이 가장 많았던 날은 언제일까?', '매일 100원씩 모으면 1년 동안 얼마나 모을 수 있을까?'와 같은 질문을 해본다면 수학적 사고력이 더 크게 확장될 수 있습니다.",
        }, {
          icon: numeracyRec02,
          pointText: '2. 만지고 경험하며 수학 개념 익히기',
          descriptionText: '직접 만지고 경험하며 수학 개념을 익히도록 합니다. 종이접기나 블록 쌓기를 통해 다양한 도형을 직접 만들어 보면 도형의 특징을 자연스럽게 알 수 있습니다. 또한, 주변에 있는 물건의 길이를 재거나 넓이를 구해보는 활동으로 측정 감각을 기를 수 있습니다. 특히, 분수가 어렵다면 모형을 활용하거나 그림으로 직접 그려보는 활동을 추천합니다. 눈으로 보고 손으로 조작하는 경험은 수학에 대한 감각과 직관을 키워주는 데 큰 도움이 됩니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 재미있는 수학 프로젝트 실천하기',
          descriptionText: "수학을 실제 생활 속 문제를 해결하는 도구로 사용해 봅니다. 가족이 좋아하는 음식, 잠드는 시간 등 재미있는 주제로 설문조사를 하고 그 결과를 표나 그래프로 정리하는 '통계 전문가' 프로젝트는 자료를 분석하고 해석하는 능력을 길러줍니다. 또한, '우리 지역 탐험가'가 되어 보는 건 어떨까요? 집에서 학교나 친구 집까지의 거리를 재보거나 어림해 보는 등 걸음 수로 거리를 재보는 것도 재미있는 활동입니다. 이러한 프로젝트를 통해 수학적 사고력, 의사소통능력, 그리고 문제해결력을 키울 수 있습니다.",
        }];
      }else if (props.gradeLevel === 'E_6') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 생활 속에서 찾는 수학의 즐거움 느끼기',
          descriptionText: '우리 주변의 다양한 상황에 숨어 있는 수학적 개념이나 원리를 찾아봅니다. 비율과 백분율을 이용해 용돈 사용 규칙을 정하거나, 요리 레시피의 양을 조절할 때, 비례식을 적용하여 계산하는 등 수학을 적극적으로 활용해 봅니다. 이러한 활동을 통해 수학의 유용성을 느끼고 문제해결력도 키울 수 있습니다. 또한, \'내 키가 매년 3cm씩 자란다면 5년 뒤의 키는 어떻게 될까?\'와 같은 질문을 만들고 답하며 규칙을 식으로 표현하는 연습을 통해 논리적으로 설명하는 힘을 기를 수 있습니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 만지고 경험하며 수학 개념 익히기',
          descriptionText: '수학 개념을 익히기 위해 직접 만들고 측정하는 활동을 해봅니다. 입체도형을 직접 만들어 부피와 겉넓이를 측정하고, 건축가가 되어 집을 설계하며 필요한 재료의 양을 계산하는 활동은 수학 개념의 이해를 돕습니다. 또한, 신문 기사나 뉴스에 나오는 통계 자료를 단순히 받아들이지 않고, \'이것은 정확할까?\', \'앞으로도 계속 그럴까?\'와 같은 질문을 던지며 비판적으로 분석해봅니다. 직접 만지고, 탐구하며 다양한 가능성을 고민하는 활동은 수학 개념을 깊이 이해하고 자료를 해석하는 힘을 길러 줄 것입니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 재미있는 수학 프로젝트 실천하기',
          descriptionText: '수학을 친구들과 함께 문제를 해결하는 도구로 사용해봅니다. 예를 들어, 우리 반 친구들이 좋아하는 스포츠처럼 재미있는 주제로 설문 조사를 하고, 엑셀 등 디지털 도구로 데이터를 정리하고 분석하는 \'데이터 분석가\' 프로젝트 활동을 통해 자료를 해석하는 힘을 기를 수 있습니다. 또한 \'수학 게임 개발자\' 프로젝트를 통해 수학적 원리를 이용한 보드게임이나 카드 게임을 직접 만들고, 게임 규칙을 정하는 활동을 하면서 논리적 사고력과 창의력을 키울 수 있습니다.',
        }];
      } else if (props.gradeLevel === 'M_2') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 실생활 문제를 수학적 언어로 표현하기',
          descriptionText: '주변에서 접하는 경기 기록, 기상 자료, 할인율, 위치 정보 등을 표, 비율, 방정식, 좌표, 그래프 등 수학적 방법으로 나타내 봅니다. 정수, 유리수의 대소 관계를 수직선 상에 표시하는 활동과 같이 일상 속 상황을 수학적 언어로 표현하면 문제의 핵심을 보다 쉽게 파악할 수 있습니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 탐구와 조작 활동으로 개념과 원리 이해하기',
          descriptionText: '개념과 원리를 효과적으로 이해하기 위해 뼈 모형의 길이나 각을 재거나 서빙 로봇 경로를 좌표평면에 나타내는 활동을 해봅니다. 정비례와 반비례, 정수와 유리수의 사칙계산, 평균을 구하거나 히스토그램, 도수분포다각형 등으로 자료를 시각화하는 활동은 원리를 스스로 발견하고 이해하는 데 도움을 줍니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 프로젝트형 활동으로 문제해결력 키우기',
          descriptionText: '학급운영비 사용 계획 세우기, 좌석 배치, 에너지 효율 비교, 주어진 조건에서 최적의 경로 찾기 등 생활 속 과제를 수학적으로 해결해 봅니다. 비율, 평균, 일차방정식, 기본 도형의 넓이와 부피, 자료 해석 등을 연결하여 적용하면 문제를 차근차근 분석하고 순서를 정해 해결해 나가는 힘을 기를 수 있습니다.',
        }, {
          icon: numeracyRec04,
          pointText: '4. 디지털 도구의 활용과 협업으로 사고 확장하기',
          descriptionText: '스프레드시트, 그래프 작성 도구, 통계 시각화 프로그램 등을 이용해 자료를 정리하고 히스토그램, 도수분포다각형 등으로 표현해 봅니다. 그 결과를 친구와 함께 분석하고 토론하거나, 영상 및 보고서로 정리해 공유하는 과정에서 다양한 관점을 접할 수 있습니다. 이러한 디지털 기반 협업 활동은 논리적 사고력과 의사소통능력을 기르는 데 효과적입니다.',
        }];
      } else if (props.gradeLevel === 'H_1') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 생활 속 데이터를 수학적 언어로 표현하기',
          descriptionText: '경기 기록, 재고 현황, 기상 데이터 등 다양한 생활 속 정보를 행렬, 유리수, 집합, 좌표, 함수의 그래프와 같은 수학적 언어로 표현하고 변환하는 활동을 해 봅니다. 추상적인 수학 개념이 구체적이고 의미 있는 도구로 활용되는 경험을 통해 수학의 유용함을 느낄 수 있습니다. 실생활 데이터를 체계적으로 분석하고 문제를 효율적으로 해결하는 것은 수리력 향상에 도움이 됩니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 탐구와 조작 활동으로 개념과 원리 이해하기',
          descriptionText: '그래프를 그리고 도형을 직접 만들어 움직여 볼 수 있는 앱을 활용해 함수의 그래프 등을 구현하고 변화 과정을 살펴봅니다. 함수의 계수를 조정해서 그래프의 모양과 위치가 어떻게 달라지는지 비교해 보고 제곱근의 크기를 수직선에 표시해 봅니다. 이렇게 직접 조작하고 관찰하는 활동은 단순히 공식을 외우는 것이 아니라 원리를 이해하고 수리적 직관을 키우는 데 도움이 됩니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 프로젝트형 활동으로 문제해결력 키우기',
          descriptionText: '물품 구입 비용이나 여행 경비 계획, 에너지 효율 분석, 복합데이터의 그래프 작성과 해석, 여러 가지 조건을 만족하는 최적의 장소 선정 등 실제 상황에서 부딪히는 과제를 수학적으로 해결해 봅니다. 이차방정식과 이차함수, 연립부등식, 통계, 기하 등을 활용하여 문제를 구조화하고 필요한 개념을 선택하여 전략을 세운 뒤 계산과 검증까지 해보는 과정은 문제해결력을 길러 줍니다.',
        }, {
          icon: numeracyRec04,
          pointText: '4. 디지털 도구의 활용과 협업으로 사고 확장하기',
          descriptionText: '스프레드시트나 그래프 작성 도구 등을 활용해 데이터를 시각화하고, 그 결과를 친구와 공유하여 토론하거나 영상 및 보고서로 함께 정리해 봅니다. 함수의 그래프 변화를 애니메이션으로 표현하거나 자료를 히스토그램, 도수분포다각형 등으로 나타내어 함께 분석해 봅니다. 이러한 활동은 다양한 관점에서 문제를 해결하는 유연성을 길러 줍니다.',
        }];
      }
      // 기본값 (초4 수리력)
      return [{
        icon: numeracyRec01,
        pointText: '1. 생활 속에서 찾는 수학의 즐거움 느끼기',
        descriptionText: "일상생활 속에서 수학 개념을 적극적으로 찾아봅니다. 계획을 세워 물건을 사거나 용돈을 관리하는 활동은 수학이 우리 생활과 얼마나 밀접한지 알게 해 줍니다. 또한, 주변의 물건을 활용해 도형 놀이를 하거나 규칙적인 변화를 찾아보는 활동은 도형의 특징을 이해하고 규칙을 발견하는 능력을 길러줍니다. '지출이 가장 많았던 날은 언제일까?', '매일 100원씩 모으면 1년 동안 얼마나 모을 수 있을까?'와 같은 질문을 해본다면 수학적 사고력이 더 크게 확장될 수 있습니다.",
      }, {
        icon: numeracyRec02,
        pointText: '2. 만지고 경험하며 수학 개념 익히기',
        descriptionText: '직접 만지고 경험하며 수학 개념을 익히도록 합니다. 종이접기나 블록 쌓기를 통해 다양한 도형을 직접 만들어 보면 도형의 특징을 자연스럽게 알 수 있습니다. 또한, 주변에 있는 물건의 길이를 재거나 넓이를 구해보는 활동으로 측정 감각을 기를 수 있습니다. 특히, 분수가 어렵다면 모형을 활용하거나 그림으로 직접 그려보는 활동을 추천합니다. 눈으로 보고 손으로 조작하는 경험은 수학에 대한 감각과 직관을 키워주는 데 큰 도움이 됩니다.',
      }, {
        icon: numeracyRec03,
        pointText: '3. 재미있는 수학 프로젝트 실천하기',
        descriptionText: "수학을 실제 생활 속 문제를 해결하는 도구로 사용해 봅니다. 가족이 좋아하는 음식, 잠드는 시간 등 재미있는 주제로 설문조사를 하고 그 결과를 표나 그래프로 정리하는 '통계 전문가' 프로젝트는 자료를 분석하고 해석하는 능력을 길러줍니다. 또한, '우리 지역 탐험가'가 되어 보는 건 어떨까요? 집에서 학교나 친구 집까지의 거리를 재보거나 어림해 보는 등 걸음 수로 거리를 재보는 것도 재미있는 활동입니다. 이러한 프로젝트를 통해 수학적 사고력, 의사소통능력, 그리고 문제해결력을 키울 수 있습니다.",
      }];
    }
  } else { 
    // 선생님용 수업 제언 - 학년별 구분
    if (props.type === 'literacy') {
      if (props.gradeLevel === 'E_4') {
        return [{
          icon: literacyRec01,
          pointText: '1. 학생의 흥미를 반영한 읽기 환경 조성하기',
          descriptionText: '학생이 흥미를 느끼거나 좋아하는 주제를 정해 관련된 글이나 책을 꾸준히 읽게 합니다. 공지문, 안내문, 뉴스, 메뉴판 등 학생들이 실제 삶에서 마주치는 다양한 글을 수업 자료로 활용합니다. 수업 시간 등을 활용해 매일 일정량의 글을 읽도록 하고, 읽은 글의 중심 내용을 요약하거나 짧은 글쓰기 활동을 제시할 수 있습니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문 생성 및 추가 자료 활용하기',
          descriptionText: '학생들이 글을 읽다가 모르는 단어나 궁금한 내용이 있을 때 학생 스스로 질문을 만들고, 사전이나 디지털 자료 등 신뢰할 수 있는 정보를 주도적으로 탐색하게 합니다. 이러한 활동은 수동적인 독서에서 능동적인 독서로 전환하는 데 효과적입니다. 질문에 대한 탐색 결과를 공유하거나 새롭게 알게 된 정보와 단어 등을 글에 적용해 보는 확장 활동으로 연결하면 좋습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 다양한 읽기 전략 활용하기',
          descriptionText: '학습 내용을 소리 내어 읽게 하는 것은 내용 이해와 집중도를 높입니다. 글을 읽으며 중요한 내용에 밑줄을 긋게 하고, 그 이유를 설명하게 하여 핵심 내용을 파악할 수 있게 합니다. 글을 읽는 과정에서 떠오르는 생각, 질문, 요점 등을 간단히 메모하도록 지도하면 내용을 이해하도록 하는 데 도움이 됩니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 학생 상호작용과 자기 생각 표현의 기회 제공하기',
          descriptionText: '글을 읽은 후 학생들이 서로의 생각을 표현하고 비교하며 이야기를 나눌 수 있는 장을 마련해 줍니다. 소그룹 토의, 짝 활동, 전체 발표 등 다양한 방식으로 질문하며 토론하는 활동을 통해 학생들의 이해 수준을 점검할 수 있습니다.',
        }];
      }else if (props.gradeLevel === 'E_6') {
        return [{
          icon: literacyRec01,
          pointText: '1. 다양한 글 읽기 환경 조성하기',
          descriptionText: '일상에서 접하는 공지문, 메뉴판, 웹사이트, 뉴스 등 다양한 매체의 글을 꾸준히 읽도록 지도합니다. 교과서나 학습 자료에 국한하지 않고, 공지문, 안내문, 뉴스, 메뉴판 등 학생들이 실제 삶에서 마주치는 다양한 글을 수업 자료로 활용합니다. 수업 시간 등을 활용해 매일 일정량의 글을 읽게 하고 읽은 글의 중심 내용을 요약하거나 자신의 생각을 표현하는 글쓰기 활동을 제시할 수 있습니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문 생성 및 추가 자료 활용하기',
          descriptionText: '학생들이 글을 읽다가 모르는 단어나 궁금한 내용이 있을 때 학생 스스로 질문을 만들고, 사전이나 디지털 자료 등 신뢰할 수 있는 정보를 주도적으로 탐색하게 합니다. 이러한 활동은 수동적인 독서에서 능동적인 독서로 전환하는 데 효과적입니다. 질문에 대한 탐색 결과를 공유하거나 새롭게 알게 된 정보와 단어 등을 글에 적용해 보는 확장 활동으로 연결하면 좋습니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 글의 중심 내용 파악과 문제 해결을 위한 읽기 전략 사용하기',
          descriptionText: '글을 소리 내어 읽고 함께 제시된 그림이나 사진을 꼼꼼하게 살펴보게 합니다. 글에서 중요한 말이나 자주 나오는 내용에 밑줄을 긋거나 표시를 하도록 합니다. 글, 그림, 도표 간의 관계를 비교하며 세부 내용을 정리하는 활동을 통해 글의 핵심을 파악하는 연습을 하도록 지도합니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 자기 생각을 표현하고 토론하는 기회 제공하기',
          descriptionText: '글쓴이의 의견이 적절한지 스스로 판단해 보고, 간단한 근거를 들어 자신의 생각을 말해보도록 합니다. 글의 목적, 주장, 근거 등을 비교하며 비판적으로 읽을 수 있도록 질문을 던지는 활동을 제시합니다. 그리고 소그룹 토의, 짝 활동, 전체 발표 등 다양한 방식으로 토론의 장을 마련합니다. 이를 통해 학생들의 이해 수준을 점검하고, 사고를 확장시키는 기회를 제공할 수 있습니다.',
        }];
      } else if (props.gradeLevel === 'M_2') {
        return [{
          icon: literacyRec01,
          pointText: '1. 수업과 일상 경험을 연결한 읽기 자료 활용하기',
          descriptionText: '일상에서 접할 수 있는 각종 안내문, 홍보 글, 카드 뉴스, 인터넷 매체의 다양한 자료를 수업 중 읽기 자료로 활용하면 학생들의 흥미를 유도할 수 있습니다. 다양한 읽기 활동을 안내하고, 읽은 글의 중심 내용을 요약하거나 주제와 관련된 다른 글이나 자료를 찾아보도록 하는 활동을 추가로 제시하는 것이 좋습니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문 생성 및 자료 탐색 활동을 수업에 포함하기',
          descriptionText: '글을 읽는 중에 모르는 단어나 궁금한 점이 생기면 스스로 질문을 만들고, 사전이나 각종 자료 등을 활용해 답을 생각할 수 있도록 하는 활동을 수업 과정에 포함하면 좋습니다. 이러한 활동은 글의 의미나 정보를 능동적으로 이해하는 데 매우 효과적입니다. 또한 읽기 중에 생긴 질문에 대한 탐색 결과를 학생들 간에 공유하여 발전적으로 적용할 수 있는 기회를 제공합니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 수업에서 다양한 읽기 전략 활용하기',
          descriptionText: '글을 읽기 전에 글의 종류나 주제와 관련된 배경지식을 충분히 떠올리도록 하고, 글을 읽으면서 새로 접한 지식이나 정보를 연결하도록 안내합니다. 글을 읽기 전에 제목, 소제목, 사진, 도표 등을 빠르게 확인하여 전체 구조를 파악하도록 한 후에, 읽기 활동을 시작하게 합니다. 글을 읽으면서 중요한 내용에 밑줄을 긋게 하고, 그 이유를 설명하게 하여, 핵심 내용을 파악하도록 합니다. 읽는 과정에서 드는 생각, 질문, 요점 등을 메모하도록 지도하여 적극적으로 글과 상호작용할 수 있도록 합니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 읽은 내용을 공유하고 자기 생각을 표현하는 기회 제공하기',
          descriptionText: '읽은 내용을 공유하고 서로의 생각을 비교하며 이야기하는 것은 글을 다양한 관점에서 이해할 수 있도록 도와줍니다. 글의 핵심 내용을 자신의 말로 요약하고 이를 논리적으로 설명하기 위해 짝 대화, 소그룹 토론, 전체 발표를 활용할 수 있습니다. 이런 활동들은 글에 대한 이해 수준을 점검하고 사고를 확장하는 기회를 제공합니다.',
        }];
      } else if (props.gradeLevel === 'H_1') {
        return [{
          icon: literacyRec01,
          pointText: '1. 수업과 일상 경험을 연결한 읽기 자료 활용하기',
          descriptionText: '일상에서 접할 수 있는 각종 안내문, 뉴스 기사, 칼럼, 인터넷 매체의 다양한 자료를 수업 중 읽기 자료로 활용하면 학생들의 흥미를 자극하고 집중력을 높일 수 있습니다. 다양한 읽기 활동을 안내하고, 읽은 글의 중심 내용을 요약하거나 관련 주제로 짧은 글을 쓰게 하는 활동을 추가로 제시하는 것이 좋습니다.',
        }, {
          icon: literacyRec02,
          pointText: '2. 질문 생성 및 자료 탐색 활동을 수업에 포함하기',
          descriptionText: '글을 읽는 중에 모르는 단어나 궁금한 점이 생기면 스스로 질문을 만들고, 사전이나 신뢰할 수 있는 각종 자료 등을 활용해 답을 찾도록 하는 활동을 수업 과정에 포함하면 좋습니다. 이러한 활동은 능동적인 탐구 중심의 독서로 전환하는 데 매우 효과적입니다. 또한 읽기 중에 생긴 질문에 대한 탐색 결과를 학생들 간에 공유하여 자신의 읽기 활동을 되돌아보고 발전적으로 적용할 수 있는 기회를 제공합니다.',
        }, {
          icon: literacyRec03,
          pointText: '3. 수업에서 다양한 읽기 전략 활용하기',
          descriptionText: '글을 읽기 전에 글의 종류나 주제와 관련된 배경지식을 충분히 떠올리도록 하고, 글을 읽으면서 새로 접한 지식이나 정보를 연결하도록 안내합니다. 글을 읽기 전에 제목, 소제목, 사진, 도표 등을 빠르게 확인하여 전체 구조를 파악하도록 한 후에, 읽기 활동을 시작하게 합니다. 글을 읽으면서 중요한 내용에 밑줄을 긋게 하고, 그 이유를 설명하게 하여, 핵심 내용을 파악하도록 합니다. 읽는 과정에서 드는 생각, 질문, 요점 등을 메모하도록 지도하여 적극적으로 글과 상호작용할 수 있도록 합니다.',
        }, {
          icon: literacyRec04,
          pointText: '4. 읽은 내용을 공유하고 자기 생각을 표현하는 기회 제공하기',
          descriptionText: '읽은 내용을 공유하고 서로의 생각을 비교하며 이야기하는 것은 글을 다양한 관점에서 이해할 수 있도록 도와줍니다. 글의 핵심 내용을 자신의 말로 요약하고 이를 논리적으로 설명하기 위해 짝 대화, 소그룹 토론, 전체 발표를 활용할 수 있습니다. 이런 활동들은 글에 대한 이해 수준을 점검하고 사고를 확장하는 기회를 제공합니다.',
        }];
      }
      // 기본값 (초4 문해력)
      return [{
        icon: literacyRec01,
        pointText: '1. 학생의 흥미를 반영한 읽기 환경 조성하기',
        descriptionText: '학생이 흥미를 느끼거나 좋아하는 주제를 정해 관련된 글이나 책을 꾸준히 읽게 합니다. 공지문, 안내문, 뉴스, 메뉴판 등 학생들이 실제 삶에서 마주치는 다양한 글을 수업 자료로 활용합니다. 수업 시간 등을 활용해 매일 일정량의 글을 읽도록 하고, 읽은 글의 중심 내용을 요약하거나 짧은 글쓰기 활동을 제시할 수 있습니다.',
      }, {
        icon: literacyRec02,
        pointText: '2. 질문 생성 및 추가 자료 활용하기',
        descriptionText: '학생들이 글을 읽다가 모르는 단어나 궁금한 내용이 있을 때 학생 스스로 질문을 만들고, 사전이나 디지털 자료 등 신뢰할 수 있는 정보를 주도적으로 탐색하게 합니다. 이러한 활동은 수동적인 독서에서 능동적인 독서로 전환하는 데 효과적입니다. 질문에 대한 탐색 결과를 공유하거나 새롭게 알게 된 정보와 단어 등을 글에 적용해 보는 확장 활동으로 연결하면 좋습니다.',
      }, {
        icon: literacyRec03,
        pointText: '3. 다양한 읽기 전략 활용하기',
        descriptionText: '학습 내용을 소리 내어 읽게 하는 것은 내용 이해와 집중도를 높입니다. 글을 읽으며 중요한 내용에 밑줄을 긋게 하고, 그 이유를 설명하게 하여 핵심 내용을 파악할 수 있게 합니다. 글을 읽는 과정에서 떠오르는 생각, 질문, 요점 등을 간단히 메모하도록 지도하면 내용을 이해하도록 하는 데 도움이 됩니다.',
      }, {
        icon: literacyRec04,
        pointText: '4. 학생 상호작용과 자기 생각 표현의 기회 제공하기',
        descriptionText: '글을 읽은 후 학생들이 서로의 생각을 표현하고 비교하며 이야기를 나눌 수 있는 장을 마련해 줍니다. 소그룹 토의, 짝 활동, 전체 발표 등 다양한 방식으로 질문하며 토론하는 활동을 통해 학생들의 이해 수준을 점검할 수 있습니다.',
      }];
    } else { 
      // 수리력 수업 제언 - 학년별
      if (props.gradeLevel === 'E_4') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 실생활 맥락을 활용한 수리적 사고 확장하기',
          descriptionText: '학생들이 수학을 실생활 맥락에서 경험하게 하여 그 유용성과 즐거움을 느끼게 하는 것이 중요합니다. 이를 위해 수업에서 삶과 연계된 다양한 활동을 적극적으로 활용하는 것을 추천합니다. 예를 들어, 물건 구매나 용돈 관리와 같이 일상생활에서 계산식을 세워 문제 해결하기, 주변 사물의 규칙이나 도형의 특징을 직접 탐구하기 등의 활동을 할 수 있습니다. 이러한 활동을 통해 학생은 스스로 질문을 만들고 답을 찾는 과정을 경험하며 수리적 사고력과 문제해결력을 향상시킬 수 있습니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 개념 형성 중심의 탐구 및 조작 활동 강화하기',
          descriptionText: '일상 소재를 활용한 탐구 및 조작 활동은 학생들의 수 감각을 키우는 효과적인 방법입니다. 예를 들어, 종이접기나 블록 쌓기로 다양한 도형의 특징을 직접 탐구하게 하고, 물건의 길이를 측정하며 넓이를 계산하는 활동은 학생들의 수 감각을 기르는 데 효과적입니다. 또한, 수 모형이나 그림을 활용해 추상적인 분수와 큰 수의 개념을 시각화하는 활동은 학생들이 수학적 흥미와 사고력을 확장하는 데 도움이 될 수 있습니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 실생활 프로젝트로 문제해결력 신장하기',
          descriptionText: "추상적인 개념을 현실 세계에 적용해 보는 프로젝트 활동은 수리적 사고를 확장하는 데 도움이 됩니다. 예를 들어, 학생들이 '통계 전문가'가 되어 스스로 만든 질문으로 설문조사를 하고 그 결과를 표나 그래프로 분석하게 할 수 있습니다. 또한, '우리 지역 탐험가'가 되어 걸음 수로 거리를 재며 측정 감각과 공간 지각 능력을 기르는 수업을 설계해 볼 수 있습니다. 이러한 활동을 통해 학생들은 스스로 질문을 만들고 답을 찾는 과정을 경험하며, 의사소통능력, 문제해결력을 기를 수 있습니다.",
        }];
      } else if (props.gradeLevel === 'E_6') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 실생활 맥락을 활용한 수리적 사고 확장하기',
          descriptionText: "학생들이 수학을 실생활의 문제를 해결하는 유용한 도구로 인식하게 하는 활동은 수리 사고력 확장에 도움이 됩니다. 이를 위해 일상에서 비율, 백분율, 비례식 등의 수학 개념을 발견하도록 하는 활동을 추천합니다. 용돈 사용 규칙을 정해 계산하거나 요리 레시피의 양을 조절하는 활동으로 수학의 유용성을 직접 느끼게 할 수 있습니다. 또한, '내 키는 어떻게 변할까?'와 같은 질문에 답하며 규칙을 식으로 표현하는 과정은 학생들의 논리적 사고력을 기르는 데 도움이 될 수 있습니다.",
        }, {
          icon: numeracyRec02,
          pointText: '2. 개념 형성 중심의 탐구 및 조작 활동 강화하기',
          descriptionText: "일상 소재를 활용한 탐구 및 조작 활동은 학생들의 수 감각을 키우는 효과적인 방법입니다. 예를 들어, 입체도형을 직접 만들어 부피와 겉넓이를 측정하거나, '건축가'가 되어 집을 설계하며 필요한 재료의 양을 계산하는 활동은 학생들의 수학적 개념 형성에 도움이 됩니다. 또한, 신문이나 뉴스에 나오는 통계 자료를 단순히 받아들이지 않고 비판적으로 분석하는 연습을 통해 자료 해석 능력을 기를 수 있습니다. 이처럼 직접 만지고 탐구하며 다양한 가능성을 고민하는 활동은 수학적 개념을 깊이 이해하는 데 도움이 될 수 있습니다.",
        }, {
          icon: numeracyRec03,
          pointText: '3. 실생활 프로젝트로 문제해결력 신장하기',
          descriptionText: "추상적인 개념을 현실 세계에 적용해 보는 프로젝트 활동은 수리적 사고를 확장하는 데 도움이 됩니다. 예를 들어, '데이터 분석가' 프로젝트를 통해 학생들이 직접 설문조사를 하고 엑셀 등 디지털 도구로 데이터를 정리, 분석하는 경험은 자료 해석을 통한 문제해결력을 기를 수 있습니다. 또한 '수학 게임 개발자' 프로젝트로 수학적 원리를 이용한 보드게임이나 카드 게임을 만드는 활동을 통해 논리적 사고력과 창의력은 물론, 친구들과 의견을 주고받으며 문제를 해결하는 의사소통 능력까지 키울 수 있습니다.",
        }];
      } else if (props.gradeLevel === 'M_2') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 실생활 문제를 수학적 언어로 표현하는 활동 설계하기',
          descriptionText: '경기 기록, 기상 자료, 할인율, 위치 정보 등 생활 속 데이터를 수학적 표현으로 바꾸어 보는 활동을 설계합니다. 표, 비율, 방정식, 좌표, 그래프 등을 활용할 수 있는 과제를 제시하여 수학 개념이 실제 생활에서 어떻게 쓰이는지를 경험하게 합니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 탐구와 조작 중심의 개념 발견 활동 포함하기',
          descriptionText: '뼈 모형의 길이나 각도 재기, 서빙 로봇의 경로를 좌표평면에 나타내기, 자료를 그래프로 표현하기와 같은 조작 활동을 수업에 포함합니다. 학생이 직접 조작하고 탐구해 보며 정비례와 반비례, 정수와 유리수의 사칙계산, 좌표를 활용한 위치와 경로 표현, 기초 통계 자료의 시각화와 같은 수학 개념을 스스로 발견하고 설명할 수 있도록 합니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 실생활 프로젝트를 통한 문제 해결 수업 설계하기',
          descriptionText: '학급운영비 사용 계획 세우기, 좌석 배치, 에너지 효율 비교, 주어진 조건에서 최적의 경로 찾기 등 실제적 과제를 프로젝트형 문제 해결 수업으로 구성합니다. 비율, 평균, 일차방정식, 도형의 넓이와 부피, 자료 해석 등의 수학적 개념을 적용해 해결 전략을 수립하고 결과를 도출하는 전 과정을 경험하도록 지도합니다. 복잡한 계산보다는 맥락 이해와 적용을 중심으로 수리적 사고의 기반을 다질 수 있도록 합니다.',
        }, {
          icon: numeracyRec04,
          pointText: '4. 디지털 도구 활용 및 협력 기반 활동으로 표현력 확장하기',
          descriptionText: '스프레드시트, 그래프 작성 도구, 통계 시각화 프로그램 등을 활용하여 자료를 분석하는 활동을 포함합니다. 히스토그램, 도수분포다각형 등을 작성하고, 그 결과를 토론, 발표, 영상 제작 등을 통해 공유하도록 합니다. 이러한 디지털 기반 협업 활동은 다양한 관점을 반영하고 논리적 표현력과 새로운 문제를 해결하는 유연성을 기르는 데 도움이 됩니다.',
        }];
      } else if (props.gradeLevel === 'H_1') {
        return [{
          icon: numeracyRec01,
          pointText: '1. 실생활 문제를 수학적 언어로 표현하는 활동 설계하기',
          descriptionText: '경기 기록, 재고 현황, 기상 데이터 등 생활 속 데이터를 행렬, 유리수, 집합, 좌표, 함수 그래프와 같은 수학적 언어로 변환해 보는 활동을 설계해 봅니다. 단순 계산이 아닌 자료의 구조를 분석하고 의미를 해석하는 과제를 제시하여 수학이 실제 문제 해결에 어떻게 쓰이는지를 경험하게 합니다.',
        }, {
          icon: numeracyRec02,
          pointText: '2. 탐구와 조작 중심의 개념 발견 활동 포함하기',
          descriptionText: '동적 수학 도구 등을 활용해 함수의 그래프와 수학적 관계를 직접 구현하고 조작하는 활동을 수업에 포함합니다. 계수를 변화시켜 그래프 모양과 위치의 변화를 비교하거나, 제곱근의 대소 관계를 시각화하는 탐구 활동을 설계합니다. 학생이 수학적 원리를 스스로 발견하고 설명하게 하는 것은 개념 이해와 수리적 직관 향상에 도움이 됩니다.',
        }, {
          icon: numeracyRec03,
          pointText: '3. 실생활 프로젝트를 통한 문제 해결 수업 설계하기',
          descriptionText: '물품 구입 비용 최적화, 에너지 효율 비교, 데이터 분석, 최적 장소 선정 등 실제 상황에서 부딪히는 과제를 프로젝트로 구성합니다. 문제 구조화–전략 수립–계산–검증–결과 해석의 전 과정을 단계별로 경험하게 하고, 함수, 방정식, 통계, 기하 등의 여러 가지 수학 개념을 적절히 적용하도록 지도합니다. 이를 통해 복합적 문제해결력을 길러 줄 수 있습니다.',
        }, {
          icon: numeracyRec04,
          pointText: '4. 디지털 도구 활용 및 협력 기반 활동으로 표현력 확장하기',
          descriptionText: '스프레드시트, 그래프 앱, 통계 시각화 도구 등을 사용하여 데이터를 수집·정리·분석하는 활동을 설계해 봅니다. 함수 변화 애니메이션, 히스토그램, 도수분포다각형 작성 등의 과제를 통해 자료를 시각화하고 해석하는 능력을 높일 수 있습니다. 학생들이 탐구 결과와 과정을 토론하고 발표하거나 영상을 제작하는 등 다양한 방식으로 공유하게 하는 것은 논리적 표현력과 협업 능력 향상에 도움이 됩니다.',
        }];
      }
      // 기본값 (초4 수리력)
      return [{
        icon: numeracyRec01,
        pointText: '1. 실생활 맥락을 활용한 수리적 사고 확장하기',
        descriptionText: '학생들이 수학을 실생활 맥락에서 경험하게 하여 그 유용성과 즐거움을 느끼게 하는 것이 중요합니다. 이를 위해 수업에서 삶과 연계된 다양한 활동을 적극적으로 활용하는 것을 추천합니다. 예를 들어, 물건 구매나 용돈 관리와 같이 일상생활에서 계산식을 세워 문제 해결하기, 주변 사물의 규칙이나 도형의 특징을 직접 탐구하기 등의 활동을 할 수 있습니다. 이러한 활동을 통해 학생은 스스로 질문을 만들고 답을 찾는 과정을 경험하며 수리적 사고력과 문제해결력을 향상시킬 수 있습니다.',
      }, {
        icon: numeracyRec02,
        pointText: '2. 개념 형성 중심의 탐구 및 조작 활동 강화하기',
        descriptionText: '일상 소재를 활용한 탐구 및 조작 활동은 학생들의 수 감각을 키우는 효과적인 방법입니다. 예를 들어, 종이접기나 블록 쌓기로 다양한 도형의 특징을 직접 탐구하게 하고, 물건의 길이를 측정하며 넓이를 계산하는 활동은 학생들의 수 감각을 기르는 데 효과적입니다. 또한, 수 모형이나 그림을 활용해 추상적인 분수와 큰 수의 개념을 시각화하는 활동은 학생들이 수학적 흥미와 사고력을 확장하는 데 도움이 될 수 있습니다.',
      }, {
        icon: numeracyRec03,
        pointText: '3. 실생활 프로젝트로 문제해결력 신장하기',
        descriptionText: "추상적인 개념을 현실 세계에 적용해 보는 프로젝트 활동은 수리적 사고를 확장하는 데 도움이 됩니다. 예를 들어, 학생들이 '통계 전문가'가 되어 스스로 만든 질문으로 설문조사를 하고 그 결과를 표나 그래프로 분석하게 할 수 있습니다. 또한, '우리 지역 탐험가'가 되어 걸음 수로 거리를 재며 측정 감각과 공간 지각 능력을 기르는 수업을 설계해 볼 수 있습니다. 이러한 활동을 통해 학생들은 스스로 질문을 만들고 답을 찾는 과정을 경험하며, 의사소통능력, 문제해결력을 기를 수 있습니다.",
      }];
    }
  }
});
</script>

<template>
  <div class="suggestion" :class="[props.type, props.region, props.gradeGroup]">
    <h5>{{ sectionTitle }}</h5>
    <ul class="pt-[10px]">
      <li v-for="(item, index) in suggestionItems" :key="index">
        <n-flex :wrap="false" :size="20" align="center">
          <img :src="item.icon" :alt="`${item.pointText}-아이콘`"/>
          <div class="text-wrap">
            <p>{{ item.pointText }}</p>
            <p>{{ item.descriptionText }}</p>
          </div>
        </n-flex>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.suggestion {
  h5 {
      text-align: center;
      font-size: 22px;
      font-weight: 500;
      font-family: var(--smoe-font-visual);
      color: #fff;
      border-radius: 10px;         
      padding: 4px 0;
  }
  &.literacy {      
      h5 { background-color: var(--result-light-blue); }
      &.seoul {
        h5 {
            position: relative;
            &::after {
              content: '';
              position: absolute;
              background-image: url("@/assets/images/result/char_03.png");
              width: 67px;
              height: 70px;
              top: 50%;
              right: 10px;
              transform: translateY(-55%);
              background-repeat: no-repeat;
              background-size: contain;
            }
        }
      }
  }
  &.numeracy {  
      h5 { background-color: var(--result-light-red); }
      &.seoul {
        h5 {
            position: relative;
            &::after {
              content: '';
              position: absolute;
              background-image: url("@/assets/images/result/char_02.png");
              width: 80px;
              height: 80px;
              top: 50%;
              right: 10px;
              transform: translateY(-55%);
              background-repeat: no-repeat;
              background-size: contain;
            }
        }
      }
  }
  &.secondary {
    padding: 0 0 8px 0 !important;
    h5 {font-size: 20px;}
    p {
      &:nth-child(1) {font-size: 18px;}
      &:nth-child(2) {font-size: 17px;}      
      }
    img {width: 80px; height: auto;}   
    ul li {padding: 8px 0;}
  }
}

.text-wrap {
    width: calc(100% - 120px);
}    
ul {
    li {
        padding: 10px 0;
        position: relative;   
        line-height: 1.4;    
        p:nth-child(1) {
            font-size: 20px;
            font-weight: 500;
            line-height: 1.5;
        }
        p:nth-child(2) {
            color: #333;
            padding-left: 20px;
            line-height: 1.3;
            font-size: 18px;
            text-align: justify;
        }
         &:before {
            content: '';
            background-image: url('@/assets/images/result/line.png'); 
            position: absolute;
            bottom: 0;
            width: 100%;
            height: 1px;
        }
        &:last-child::before {
            display: none;
        }        
    }
}
</style>